// frontend/src/tabs/CloseTabsOnStoreChange.jsx
//
// Headless. Collapses the workspace to Home when the selected store changes
// underneath a LIVE document.
//
// A tab belongs to one store. Its path, its per-tab filter slots and its grid
// state were all opened against whichever store was selected at the time, and
// a deep route carries an id from that store's data (/purchase-orders/<id>).
// Left open across a store change, those tabs address records the new store
// does not have.
//
// The window that performs the switch handles itself: it confirms with the
// merchant, closes the non-Home tabs and restarts the document on Home
// (useStoreSwitch → AuthContext.switchStore). This component covers every OTHER
// way the selection can move under a document that is not being replaced —
// today a switch made in another browser window, which converges this one in
// place through the 'storage' listener → refreshUser → applyUserContext.
//
// It reacts to `selectedStoreId` rather than to that storage event, so any
// path that re-points the store under a live document closes the stale
// tabs.
//
// Mounted inside <TabsProvider> (App.jsx) — AuthProvider wraps the tabbed
// shell from above and cannot reach tab state, so the reaction has to live on
// this side of the boundary.

import { useAuth } from '../contexts/AuthContext';
import { useChangeEffect } from '../hooks/useChangeEffect';
import { useTabs } from './useTabs';

export default function CloseTabsOnStoreChange() {
  const { selectedStoreId } = useAuth();
  const { closeAllExceptHome } = useTabs();

  // useChangeEffect, not useEffect([selectedStoreId]): the first value is the
  // baseline. On a reload or a deep link the restored tabs belong to the store
  // that is already selected, and wiping them would break F5 restore.
  //
  // The path closeAllExceptHome returns is for callers that replace the
  // document; this one stays put. Focus lands on Home, and TabsHost's
  // activeId → URL effect navigates there — the single seam for that
  // direction, so no navigate() call belongs here.
  useChangeEffect(selectedStoreId, closeAllExceptHome);

  return null;
}
